import {
  ImageSliceView,
  ImageSliceViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { getCreativeSuffix } from 'src/lib/utils';
import popups from 'src/lib/ui/config/popups';
import { Pet, PetSpecies } from 'src/replicant/buruburu/pet';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import { getTimestampText } from '../visit.util';
import { Feature } from 'src/replicant/analytics';

type Player = {
  id: string;
  name?: string;
  photo?: string;
  species: PetSpecies;
};
type Friend = Player & {
  lastLogin: number;
};
type Visitor = Player & {
  lastVisit: number;
};
const playerIsVisitor = (player: Player): player is Visitor =>
  'lastVisit' in player;

namespace style {
  export const container: Omit<ButtonScaleViewOpts, 'tracking'> = {
    offsetX: 20,
    offsetY: 10,
    pressedOffsetY: 5,
    width: popups.buruburu.width - 100,
    height: 114,
  };

  export const tile: ImageSliceViewOpts = {
    image: 'assets/ui/shared/lossless/scroller_panel_blue.png',
    width: container.width,
    height: container.height,
    slices: {
      top: 19,
      left: 19,
      right: 22,
      bot: 28,
    },
  };

  export const image: ImageScaleViewOpts = {
    scaleMethod: 'contain',
    width: 78,
    height: 78,
    x: 16,
    y: 15,
  };

  export const name: LangBitmapOpts = {
    size: 30,
    font: 'Body',
    color: '#9A7171',
    verticalAlign: 'top',
    width: 211,
    height: 48,
    x: 115,
    y: 18,
  };

  export const speciesLabel: LangBitmapOpts = {
    size: 22,
    font: 'Body',
    color: '#7191AB',
    x: name.x,
    y: 59,
    width: 190,
    height: 34,
    align: 'left',
  };

  export const speciesIconBackdrop: ImageViewOpts = {
    image: 'assets/ui/shared/lossless/backdrop_panel.png',
    x: image.x + image.width,
    y: image.y + image.height,
    width: 128,
    height: 133,
    scale: 0.33,
  };
  speciesIconBackdrop.x -=
    speciesIconBackdrop.width * speciesIconBackdrop.scale * 0.7;
  speciesIconBackdrop.y -=
    speciesIconBackdrop.height * speciesIconBackdrop.scale * 0.9;

  export const speciesIcon: ImageScaleViewOpts = {
    width: speciesIconBackdrop.width,
    height: speciesIconBackdrop.height,
    scaleMethod: 'contain',
  };

  export const lastLogin: LangBitmapOpts = {
    font: speciesLabel.font,
    color: speciesLabel.color,
    y: name.y,
    height: speciesLabel.y + speciesLabel.height - name.y,
    width: 160,
    size: 20,
    align: 'right',
    verticalAlign: 'center',
  };
  lastLogin.x = container.width - container.offsetX - lastLogin.width;
}

export class VisitFriendTile {
  readonly view: ButtonScaleView;

  constructor(opts: {
    friend: Friend | Visitor;
    onClick: () => Promise<void>;
  }) {
    this.view = new ButtonScaleView({
      ...style.container,
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_VISIT_FRIEND.SELECT,
      },
      onClick: opts.onClick,
    });

    // background
    this.view.addSubview(new ImageSliceView(style.tile));

    // icon
    this.view.addSubview(
      new ImageScaleView({
        ...style.image,
        image:
          opts.friend.photo || 'assets/ui/shared/icons/icon_avatar_generic.png',
      }),
    );

    // name
    this.view.addSubview(
      new LangBitmapFontTextView({
        ...style.name,
        width: style.container.width - 268,
        localeText: () => opts.friend.name || '',
      }),
    );

    // species name
    const { species } = opts.friend;
    this.view.addSubview(
      new LangBitmapFontTextView({
        ...style.speciesLabel,
        localeText: () =>
          Pet.ruleset.evolutions[species]?.[`name_${getCreativeSuffix()}`],
      }),
    );

    // species icon
    const speciesIcon = this.view.addSubview(
      new ImageView(style.speciesIconBackdrop),
    );
    speciesIcon.addSubview(
      new ImageScaleView({
        ...style.speciesIcon,
        image: `assets/species/${species}.png`,
      }),
    );

    // timestamp
    this.view.addSubview(
      new LangBitmapFontTextView({
        ...style.lastLogin,
        localeText: () =>
          playerIsVisitor(opts.friend)
            ? `${i18n('friendsVisit.popup.lastVisit')}\n${getTimestampText(
                opts.friend.lastVisit,
              )}`
            : `${i18n('friendsVisit.popup.lastLogin')}\n${getTimestampText(
                opts.friend.lastLogin,
              )}`,
      }),
    );
  }
}
